import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { config } from "../config";
import { useEventsApi } from "../hooks/useEventsApi";

export type ExportCodePreferenceContextValue = {
  /** When true, exports use attributes.event_code_old for non-manufacturer events (server global). */
  useLegacyEventCodesInExport: boolean;
  setUseLegacyEventCodesInExport: (value: boolean) => Promise<void>;
  /** Matches server; for optional client query params (server ignores and uses DB). */
  exportUseOldCodeParam: "true" | "false";
  wordingGuardEnabled: boolean;
  setWordingGuardEnabled: (value: boolean) => Promise<void>;
  textSuggestionsEnabled: boolean;
  setTextSuggestionsEnabled: (value: boolean) => Promise<void>;
  settingsLoaded: boolean;
  settingsLoadError: string | null;
};

const ExportCodePreferenceContext =
  createContext<ExportCodePreferenceContextValue | null>(null);

export function ExportCodePreferenceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { callApi } = useEventsApi();
  const [useLegacy, setUseLegacy] = useState(true);
  const [wordingGuardEnabled, setWordingGuardEnabledState] = useState(true);
  const [textSuggestionsEnabled, setTextSuggestionsEnabledState] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsLoadError, setSettingsLoadError] = useState<string | null>(null);
  const useLegacyRef = useRef(useLegacy);
  const wordingGuardEnabledRef = useRef(wordingGuardEnabled);
  const textSuggestionsEnabledRef = useRef(textSuggestionsEnabled);
  useEffect(() => {
    useLegacyRef.current = useLegacy;
  }, [useLegacy]);
  useEffect(() => {
    wordingGuardEnabledRef.current = wordingGuardEnabled;
  }, [wordingGuardEnabled]);
  useEffect(() => {
    textSuggestionsEnabledRef.current = textSuggestionsEnabled;
  }, [textSuggestionsEnabled]);

  const refresh = useCallback(async () => {
    try {
      const data = await callApi(`${config.apiUrl}/api/export-settings/`, "GET");
      if (
        data &&
        typeof data === "object" &&
        typeof (data as { use_legacy_event_codes?: unknown }).use_legacy_event_codes ===
          "boolean"
      ) {
        setUseLegacy((data as { use_legacy_event_codes: boolean }).use_legacy_event_codes);
      }
      if (
        data &&
        typeof data === "object" &&
        typeof (data as { wording_guard_enabled?: unknown }).wording_guard_enabled === "boolean"
      ) {
        setWordingGuardEnabledState(
          (data as { wording_guard_enabled: boolean }).wording_guard_enabled
        );
      }
      if (
        data &&
        typeof data === "object" &&
        typeof (data as { text_suggestions_enabled?: unknown }).text_suggestions_enabled === "boolean"
      ) {
        setTextSuggestionsEnabledState(
          (data as { text_suggestions_enabled: boolean }).text_suggestions_enabled
        );
      }
      setSettingsLoadError(null);
    } catch {
      // Keep UI usable with local defaults and expose a readable error.
      setSettingsLoadError(
        "Could not reach backend settings endpoint. Some admin settings may be unavailable."
      );
    } finally {
      setSettingsLoaded(true);
    }
  }, [callApi]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  const setUseLegacyEventCodesInExport = useCallback(async (value: boolean) => {
    const previous = useLegacyRef.current;
    setUseLegacy(value);
    try {
      await callApi(`${config.apiUrl}/api/export-settings/`, "PATCH", {
        use_legacy_event_codes: value,
      });
    } catch {
      setUseLegacy(previous);
      throw new Error("Failed to save export settings.");
    }
  }, [callApi]);

  const setWordingGuardEnabled = useCallback(async (value: boolean) => {
    const previous = wordingGuardEnabledRef.current;
    setWordingGuardEnabledState(value);
    try {
      await callApi(`${config.apiUrl}/api/export-settings/`, "PATCH", {
        wording_guard_enabled: value,
      });
    } catch {
      setWordingGuardEnabledState(previous);
      throw new Error("Failed to save wording guard settings.");
    }
  }, [callApi]);

  const setTextSuggestionsEnabled = useCallback(async (value: boolean) => {
    const previous = textSuggestionsEnabledRef.current;
    setTextSuggestionsEnabledState(value);
    try {
      await callApi(`${config.apiUrl}/api/export-settings/`, "PATCH", {
        text_suggestions_enabled: value,
      });
    } catch {
      setTextSuggestionsEnabledState(previous);
      throw new Error("Failed to save text suggestions settings.");
    }
  }, [callApi]);

  const value = useMemo<ExportCodePreferenceContextValue>(
    () => ({
      useLegacyEventCodesInExport: useLegacy,
      setUseLegacyEventCodesInExport,
      exportUseOldCodeParam: useLegacy ? "true" : "false",
      wordingGuardEnabled,
      setWordingGuardEnabled,
      textSuggestionsEnabled,
      setTextSuggestionsEnabled,
      settingsLoaded,
      settingsLoadError,
    }),
    [
      useLegacy,
      setUseLegacyEventCodesInExport,
      wordingGuardEnabled,
      setWordingGuardEnabled,
      textSuggestionsEnabled,
      setTextSuggestionsEnabled,
      settingsLoaded,
      settingsLoadError,
    ]
  );

  return (
    <ExportCodePreferenceContext.Provider value={value}>
      {children}
    </ExportCodePreferenceContext.Provider>
  );
}

export function useExportCodePreference(): ExportCodePreferenceContextValue {
  const ctx = useContext(ExportCodePreferenceContext);
  if (!ctx) {
    throw new Error(
      "useExportCodePreference must be used within ExportCodePreferenceProvider"
    );
  }
  return ctx;
}
